import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, RefreshCw, ChevronLeft, ChevronRight, Eye, Pencil,
  Plus, Trash2, Search, X, Save, ScanEye, GripVertical, Globe, Database,
  Mail, Copy, Check, Ban
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { adminManagementApi } from '../../services/api';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [listCollapsed, setListCollapsed] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());

  // Instance role editing
  const [addingRole, setAddingRole] = useState(null);
  const [selectedDbRole, setSelectedDbRole] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [dirtyRoles, setDirtyRoles] = useState({});
  const [selectedOverride, setSelectedOverride] = useState(null);

  // Permission overrides
  const [allPermissions, setAllPermissions] = useState([]);
  const [resolvedPerms, setResolvedPerms] = useState(null);
  const [showEffective, setShowEffective] = useState(false);
  const [hideEffectiveDenied, setHideEffectiveDenied] = useState(false);

  // Copy from user
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyDbScope, setCopyDbScope] = useState('all');
  const [copySaving, setCopySaving] = useState(false);

  // Instance permission add
  const [addingPerm, setAddingPerm] = useState(false);
  const [newPermDefId, setNewPermDefId] = useState('');

  // Global permissions
  const [showGlobalPerms, setShowGlobalPerms] = useState(false);
  const [globalPerms, setGlobalPerms] = useState([]);
  const [addingGlobalPerm, setAddingGlobalPerm] = useState(false);
  const [newGlobalPermDefId, setNewGlobalPermDefId] = useState('');

  // New user modal
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', organizationId: '', defaultDatabaseId: '', defaultRole: 'User' });
  const [newUserSaving, setNewUserSaving] = useState(false);

  // Invitation state
  const [invitation, setInvitation] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  // Action menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { notify } = useNotifications();
  const { impersonate, users: authUsers, currentUser } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, summaryRes, dbRes, orgRes, permRes] = await Promise.all([
        adminManagementApi.getUsers(),
        adminManagementApi.getSummary(),
        adminManagementApi.getDatabases(),
        adminManagementApi.getOrganizations(),
        adminManagementApi.getPermissions()
      ]);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
      setDatabases(dbRes.data);
      setOrganizations(orgRes.data);
      setAllPermissions(permRes.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadInvitation = async (userId) => {
    try {
      const res = await adminManagementApi.getInvitation(userId);
      setInvitation(res.data);
    } catch {
      setInvitation(null);
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await adminManagementApi.getUser(userId);
      setUserDetail(res.data);
      setSelectedDbRole(null);
      setResolvedPerms(null);
      setEditing(null);
      setDirtyFields(new Set());
      setDirtyRoles({});
      setEditingRoleId(null);
    } catch (err) {
      console.error('Failed to load user detail:', err);
    }
  };

  const loadResolvedPerms = async (userId, databaseId) => {
    try {
      const res = await adminManagementApi.resolvePermissions(userId, databaseId);
      setResolvedPerms(res.data);
    } catch (err) {
      console.error('Failed to resolve permissions:', err);
    }
  };

  const loadGlobalPerms = async (userId) => {
    try {
      const res = await adminManagementApi.getGlobalPermissions(userId);
      setGlobalPerms(res.data);
    } catch (err) {
      console.error('Failed to load global permissions:', err);
    }
  };

  const handleAddGlobalPerm = async () => {
    if (!newGlobalPermDefId || !selected) return;
    try {
      await adminManagementApi.addGlobalPermission(selected.id, { PermissionDefId: parseInt(newGlobalPermDefId), AccessType: 'Grant' });
      setAddingGlobalPerm(false);
      setNewGlobalPermDefId('');
      await loadGlobalPerms(selected.id);
    } catch (err) {
      console.error('Add global permission failed:', err);
    }
  };

  const handleAddInstancePerm = async () => {
    if (!newPermDefId || !selectedDbRole) return;
    try {
      await adminManagementApi.createUserPermission({ UserRoleId: selectedDbRole.id, PermissionDefId: parseInt(newPermDefId), AccessType: 'Grant' });
      setAddingPerm(false);
      setNewPermDefId('');
      await loadUserDetail(selected.id);
      await loadResolvedPerms(selected.id, selectedDbRole.databaseId);
    } catch (err) {
      console.error('Add permission failed:', err);
    }
  };

  const handleDeleteOverride = () => {
    if (!selectedOverride || !selectedDbRole) return;
    const match = selectedDbRole.overrides?.find(o => o.permissionKey === selectedOverride.permissionKey);
    if (!match) return;
    notify.confirm(`Remove permission override for "${selectedOverride.permissionKey}"?`, async () => {
      try {
        await adminManagementApi.deleteUserPermission(match.id);
        setSelectedOverride(null);
        await loadUserDetail(selected.id);
        await loadResolvedPerms(selected.id, selectedDbRole.databaseId);
      } catch (err) {
        console.error('Delete override failed:', err);
      }
    });
  };

  const handleDeleteGlobalPerm = (gp) => {
    notify.confirm(`Remove global permission "${gp.displayName}" from ${userDetail?.displayName}?`, async () => {
      try {
        await adminManagementApi.deleteGlobalPermission(gp.id);
        await loadGlobalPerms(selected.id);
      } catch (err) {
        console.error('Delete global permission failed:', err);
      }
    });
  };

  const handleSelectUser = (user) => {
    setSelected(user);
    setAddingRole(null);
    setSelectedDbRole(null);
    setResolvedPerms(null);
    setShowCopyModal(false);
    setEditing(null);
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
    setSelectedOverride(null);
    setShowGlobalPerms(false);
    setGlobalPerms([]);
    setAddingGlobalPerm(false);
    setNewGlobalPermDefId('');
    setAddingPerm(false);
    setNewPermDefId('');
    loadUserDetail(user.id);
    loadGlobalPerms(user.id);
    loadInvitation(user.id);
    setInvitation(null);
    setInviteLink(null);
    setInviteCopied(false);
  };

  // Edit mode
  const startEdit = () => {
    if (!userDetail) return;
    setEditing({
      displayName: userDetail.displayName,
      email: userDetail.email || '',
      role: userDetail.role,
      organizationId: userDetail.organizationId,
      isActive: userDetail.isActive
    });
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
  };

  const cancelEdit = (force = false) => {
    if (!force && (dirtyFields.size > 0 || Object.keys(dirtyRoles).length > 0)) {
      notify.confirm('Discard unsaved changes?', () => {
        setEditing(null);
        setDirtyFields(new Set());
        setDirtyRoles({});
        setEditingRoleId(null);
      });
      return;
    }
    setEditing(null);
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = field === 'organizationId' ? userDetail.organizationId
      : field === 'isActive' ? userDetail.isActive
      : field === 'role' ? userDetail.role
      : field === 'displayName' ? userDetail.displayName
      : userDetail.email || '';
    const newDirty = new Set(dirtyFields);
    if (value !== original) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || (dirtyFields.size === 0 && Object.keys(dirtyRoles).length === 0)) return;
    setSaving(true);
    try {
      if (dirtyFields.size > 0) {
        const updates = {};
        if (dirtyFields.has('displayName')) updates.DisplayName = editing.displayName;
        if (dirtyFields.has('email')) updates.Email = editing.email;
        if (dirtyFields.has('role')) updates.Role = editing.role;
        if (dirtyFields.has('organizationId')) updates.OrganizationId = editing.organizationId;
        if (dirtyFields.has('isActive')) updates.IsActive = editing.isActive;
        await adminManagementApi.updateUser(userDetail.id, updates);
      }
      for (const [userRoleId, newRole] of Object.entries(dirtyRoles)) {
        const dr = userDetail.databaseRoles?.find(r => r.id === parseInt(userRoleId));
        if (dr) {
          await adminManagementApi.updateUserRole(parseInt(userRoleId), { UserId: userDetail.id, DatabaseId: dr.databaseId, Role: newRole });
        }
      }
      setEditing(null);
      setDirtyFields(new Set());
      setDirtyRoles({});
      setEditingRoleId(null);
      await load();
      await loadUserDetail(userDetail.id);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = () => {
    if (!userDetail || !isEditing) return;
    const roleCount = userDetail.databaseRoles?.length || 0;
    const overrideCount = userDetail.databaseRoles?.reduce((sum, dr) => sum + (dr.overrides?.length || 0), 0) || 0;
    const msg = `Delete ${userDetail.displayName}?\n\nThis will remove:\n- The user record\n- ${roleCount} role${roleCount !== 1 ? 's' : ''}\n- ${overrideCount} permission${overrideCount !== 1 ? 's' : ''}`;
    notify.confirm(msg, async () => {
      setSaving(true);
      try {
        for (const dr of (userDetail.databaseRoles || [])) {
          for (const o of (dr.overrides || [])) await adminManagementApi.deleteUserPermission(o.id);
          await adminManagementApi.deleteUserRole(dr.id);
        }
        await adminManagementApi.updateUser(userDetail.id, { IsActive: false });
        setSelected(null);
        setUserDetail(null);
        setEditing(null);
        setDirtyFields(new Set());
        await load();
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setSaving(false);
      }
    }, { onCancel: () => cancelEdit() });
  };

  const handleSelectDbRole = (dr) => {
    setSelectedDbRole(dr);
    setSelectedOverride(null);
    if (selected) loadResolvedPerms(selected.id, dr.databaseId);
  };

  const handleAddUserRole = async () => {
    if (!addingRole || !selected || !addingRole.DatabaseId) return;
    try {
      await adminManagementApi.createUserRole(addingRole);
      setAddingRole(null);
      await loadUserDetail(selected.id);
      await load();
    } catch (err) {
      console.error('Add role failed:', err);
    }
  };

  const handleDeleteUserRole = (roleId) => {
    const dr = userDetail?.databaseRoles?.find(r => r.id === roleId);
    notify.confirm(`Remove ${dr?.databaseName || ''} role and all its permissions?`, async () => {
      try {
        await adminManagementApi.deleteUserRole(roleId);
        setSelectedDbRole(null);
        setResolvedPerms(null);
        await loadUserDetail(selected.id);
        await load();
      } catch (err) {
        console.error('Delete role failed:', err);
      }
    });
  };

  // Re-select same DB role after userDetail refresh
  useEffect(() => {
    if (selectedDbRole && userDetail?.databaseRoles) {
      const refreshed = userDetail.databaseRoles.find(dr => dr.id === selectedDbRole.id);
      if (refreshed) setSelectedDbRole(refreshed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail]);

  const handleCopyFrom = async () => {
    if (!copySource || !selected) return;
    setCopySaving(true);
    try {
      if (copyDbScope === 'all') {
        await adminManagementApi.copyUserRoles(copySource.id, selected.id);
      } else {
        const sourceDetail = await adminManagementApi.getUser(copySource.id);
        const sourceDbRole = sourceDetail.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (!sourceDbRole) { notify.warning(`${copySource.displayName} has no role for that database.`); setCopySaving(false); return; }
        let targetDbRole = userDetail.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole) {
          await adminManagementApi.updateUserRole(targetDbRole.id, { UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        } else {
          await adminManagementApi.createUserRole({ UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        }
        const refreshed = await adminManagementApi.getUser(selected.id);
        targetDbRole = refreshed.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole && sourceDbRole.overrides?.length > 0) {
          for (const o of (targetDbRole.overrides || [])) await adminManagementApi.deleteUserPermission(o.id);
          for (const o of sourceDbRole.overrides) await adminManagementApi.createUserPermission({ UserRoleId: targetDbRole.id, PermissionDefId: o.permissionDefId, AccessType: o.accessType });
        }
      }
      setShowCopyModal(false);
      setCopySource(null);
      await load();
      await loadUserDetail(selected.id);
    } catch (err) {
      console.error('Copy failed:', err);
    } finally {
      setCopySaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim()) return;
    setNewUserSaving(true);
    try {
      await adminManagementApi.createUser({
        firstName:         newUser.firstName.trim(),
        lastName:          newUser.lastName.trim(),
        email:             newUser.email.trim(),
        organizationId:    newUser.organizationId ? parseInt(newUser.organizationId) : null,
        defaultDatabaseId: newUser.defaultDatabaseId ? parseInt(newUser.defaultDatabaseId) : null,
        defaultRole:       newUser.defaultRole || null,
      });
      setShowNewUser(false);
      setNewUser({ firstName: '', lastName: '', email: '', organizationId: '', defaultDatabaseId: '', defaultRole: 'User' });
      await load();
      notify.success('User created');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setNewUserSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!selected) return;
    try {
      const res = await adminManagementApi.sendInvite(selected.id);
      setInvitation({ status: 'pending', expiresAt: res.data.expiresAt, sentAt: res.data.sentAt });
      setInviteLink(res.data.inviteUrl);
      if (res.data.emailSent === false) {
        notify.warning('Invitation saved — email delivery failed. Copy the link from the 6-dot menu to send manually.');
      } else {
        notify.success('Invitation sent');
      }
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to send invitation');
    }
  };

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    });
  };

  const handleSendTestEmail = async () => {
    if (!selected) return;
    setMenuOpen(false);
    try {
      const res = await adminManagementApi.sendTestEmail(selected.id);
      if (res.data.sent === false) {
        notify.error(`Test email failed: ${res.data.error}`);
      } else {
        notify.success(`Test email sent to ${userDetail?.email || ''}`);
      }
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to send test email');
    }
  };

  const handleRevokeClick = () => {
    if (!selected || !userDetail) return;
    setMenuOpen(false);
    const isSelf = selected.id === currentUser?.id;
    const isSysAdmin = userDetail.isSysAdmin;
    const displayName = userDetail.displayName;
    const msg = isSelf && isSysAdmin
      ? 'You are revoking your own SysAdmin account. You will be immediately locked out of DAWN.'
      : isSelf
        ? 'You are revoking your own account. You will be immediately locked out of DAWN.'
        : isSysAdmin
          ? `This will revoke a SysAdmin account. ${displayName} will be locked out immediately.`
          : `This will remove ${displayName}'s access and clear all invitation records. They will need to be re-invited to return.`;
    notify.confirm(msg, async () => {
      try {
        await adminManagementApi.revokeInvite(selected.id);
        setInvitation({ status: 'none' });
        setInviteLink(null);
        setUserDetail(prev => prev ? { ...prev, azureObjectId: null } : prev);
        notify.success(`Access revoked for ${displayName}`);
      } catch (err) {
        notify.error(err.response?.data?.error || 'Failed to revoke access');
      }
    });
  };

  const filteredUsers = users.filter(u =>
    !searchTerm ||
    u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0 || Object.keys(dirtyRoles).length > 0;

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Users size={20} />
          <h1>Admin Users</h1>
          <span className="stui-module-header-meta">
            {summary ? `${summary.activeUsers} active / ${summary.users} total` : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* CRUD: + or Trash depending on edit state */}
          {isEditing ? (
            <button className="stui-crud-btn" onClick={handleDeleteUser} disabled={saving} title="Delete user" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="stui-crud-btn" onClick={() => setShowNewUser(true)} title="Add user"><Plus size={18} /></button>
          )}
          {/* Edit/Cancel toggle */}
          <button
            className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? cancelEdit : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          {/* Save */}
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          {/* Action menu */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className={`stui-action-menu-item ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={() => {
                  setMenuOpen(false);
                  const authUser = authUsers.find(u => u.id === selected?.id);
                  if (authUser) impersonate(authUser);
                }}>
                  <Users size={16} /><span>Impersonate User</span>
                </button>
                <button className={`stui-action-menu-item ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={() => { setMenuOpen(false); setShowCopyModal(true); }}>
                  <Database size={16} /><span>Copy Roles From...</span>
                </button>
                {selected && (<>
                  <div className="stui-action-menu-divider" />
                  {/* Invite / resend / copy — only when not yet accepted */}
                  {!userDetail?.azureObjectId && (<>
                    {(!invitation || invitation.status === 'none' || invitation.status === 'expired') && (
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleSendInvite(); }}>
                        <Mail size={16} /><span>{invitation?.status === 'expired' ? 'Resend Invite' : 'Send Invite'}</span>
                      </button>
                    )}
                    {invitation?.status === 'pending' && (<>
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleSendInvite(); }}>
                        <Mail size={16} /><span>Resend Invite</span>
                      </button>
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleCopyLink(); }} disabled={!inviteLink}>
                        {inviteCopied ? <Check size={16} /> : <Copy size={16} />}
                        <span>{inviteCopied ? 'Copied!' : 'Copy Invite Link'}</span>
                      </button>
                    </>)}
                  </>)}
                  <button className="stui-action-menu-item" onClick={handleSendTestEmail}>
                    <Mail size={16} /><span>Send Test Email</span>
                  </button>
                  {/* Revoke — available any time there is something to revoke */}
                  {(userDetail?.azureObjectId || (invitation && invitation.status !== 'none')) && (
                    <button className="stui-action-menu-item danger" onClick={handleRevokeClick}>
                      <Ban size={16} /><span>Revoke Access</span>
                    </button>
                  )}
                </>)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand list"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse list"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users..." />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filteredUsers.length === 0 ? (
                <div className="stui-list-empty">No users found</div>
              ) : filteredUsers.map(u => (
                <button key={u.id} onClick={() => handleSelectUser(u)} className={`stui-list-item ${selected?.id === u.id ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">{u.displayName}</span>
                  <span className="stui-list-item-secondary">{u.email || 'No email'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="stui-detail-panel">
          {selected && userDetail ? (
            <div className="stui-detail-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* USER INFORMATION */}
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">User Information</div></div>
                <div className="stui-field-group stui-col-8">
                  <label>Display Name</label>
                  <input className={`stui-field-input${isEditing && dirtyFields.has('displayName') ? ' dirty' : ''}`} value={isEditing ? editing.displayName : userDetail.displayName} onChange={isEditing ? e => handleFieldChange('displayName', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Email</label>
                  <input className={`stui-field-input${isEditing && dirtyFields.has('email') ? ' dirty' : ''}`} value={isEditing ? editing.email : (userDetail.email || '')} onChange={isEditing ? e => handleFieldChange('email', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>SysAdmin</label>
                  <div style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" checked={isEditing ? !!editing.isSysAdmin : !!userDetail.isSysAdmin} onChange={isEditing ? e => handleFieldChange('isSysAdmin', e.target.checked) : undefined} disabled={!isEditing} style={{ width: '1rem', height: '1rem' }} />
                    {isEditing && dirtyFields.has('role') && <span style={{ color: '#f9a825', fontWeight: 600, fontSize: 'var(--text-xs)', marginLeft: '4px' }}>changed</span>}
                  </div>
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Active</label>
                  <div style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" checked={isEditing ? editing.isActive : userDetail.isActive} onChange={isEditing ? e => handleFieldChange('isActive', e.target.checked) : undefined} disabled={!isEditing} style={{ width: '1rem', height: '1rem' }} />
                    {isEditing && dirtyFields.has('isActive') && <span style={{ color: '#f9a825', fontWeight: 600, fontSize: 'var(--text-xs)', marginLeft: '4px' }}>changed</span>}
                  </div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Organization</label>
                  {isEditing ? (
                    <select className={`stui-field-input${dirtyFields.has('organizationId') ? ' dirty' : ''}`} value={editing.organizationId || ''} onChange={e => handleFieldChange('organizationId', e.target.value ? parseInt(e.target.value) : null)}>
                      <option value="">-- None --</option>
                      {organizations.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
                    </select>
                  ) : (
                    <input className="stui-field-input" value={userDetail.organizationName || ''} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Last Login</label>
                  <input className="stui-field-input" value={userDetail.lastLogin ? new Date(userDetail.lastLogin).toLocaleString() : 'Never'} readOnly />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Created</label>
                  <input className="stui-field-input" value={userDetail.createdAt ? new Date(userDetail.createdAt).toLocaleDateString() : ''} readOnly />
                </div>
                <div className="stui-field-group stui-col-14">
                  <label>Azure Object ID</label>
                  <input className="stui-field-input" value={userDetail.azureObjectId || ''} readOnly style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }} />
                </div>
                <div className="stui-field-group stui-col-10">
                  <label>Invite Status</label>
                  <div style={{ paddingTop: '4px' }}>
                    {userDetail.azureObjectId ? (
                      <span className="stui-badge stui-badge-accepted">Accepted</span>
                    ) : !invitation || invitation.status === 'none' ? (
                      <span className="stui-badge stui-badge-none">Not Invited</span>
                    ) : invitation.status === 'pending' ? (
                      <span className="stui-badge stui-badge-pending">Invite Sent</span>
                    ) : invitation.status === 'expired' ? (
                      <span className="stui-badge stui-badge-expired">Expired</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* INSTANCE ROLES / GLOBAL PERMISSIONS (toggled by globe) */}
              <div className="stui-related-section" style={{ height: '200px', flexShrink: 0 }}>
                <div className="stui-detail-section-header stui-related-header" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Left: title or inline-add dropdowns */}
                  <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {!addingRole && !addingGlobalPerm && (
                      <span style={{ flexShrink: 0 }}>{showGlobalPerms ? `Global Permissions (${globalPerms.length})` : `Instance Roles (${userDetail.databaseRoles?.length || 0})`}</span>
                    )}
                    {!showGlobalPerms && addingRole && (
                      <>
                        <select className="stui-field-input" value={addingRole.DatabaseId} onChange={e => setAddingRole({ ...addingRole, DatabaseId: parseInt(e.target.value) })} style={{ flex: 1, height: '24px', padding: '0 4px', fontSize: 'var(--text-xs)' }}>
                          <option value={0}>Select instance...</option>
                          {databases.filter(d => !userDetail.databaseRoles?.some(dr => dr.databaseId === d.id)).map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
                        </select>
                        <select className="stui-field-input" value={addingRole.Role} onChange={e => setAddingRole({ ...addingRole, Role: e.target.value })} style={{ width: '96px', height: '24px', padding: '0 4px', fontSize: 'var(--text-xs)' }}>
                          <option value="Guest">Guest</option><option value="User">User</option><option value="PowerUser">PowerUser</option><option value="AppAdmin">AppAdmin</option>
                        </select>
                      </>
                    )}
                    {showGlobalPerms && addingGlobalPerm && (
                      <select className="stui-field-input" value={newGlobalPermDefId} onChange={e => setNewGlobalPermDefId(e.target.value)} style={{ flex: 1, height: '24px', padding: '0 4px', fontSize: 'var(--text-xs)' }}>
                        <option value="">Select permission...</option>
                        {allPermissions.filter(p => !globalPerms.some(gp => gp.permissionDefId === p.id)).map(p => <option key={p.id} value={p.id}>{p.displayName} ({p.category})</option>)}
                      </select>
                    )}
                  </div>
                  {/* Right cluster: Trash | Save+Cancel or Plus — immediately left of Globe */}
                  {!showGlobalPerms && !addingRole && isEditing && selectedDbRole && (
                    <button onClick={() => handleDeleteUserRole(selectedDbRole.id)} title="Remove selected role" className="stui-related-header-btn danger"><Trash2 size={14} /></button>
                  )}
                  {!showGlobalPerms && addingRole && (
                    <>
                      <button onClick={handleAddUserRole} disabled={!addingRole.DatabaseId} className="stui-related-header-btn"><Save size={13} /></button>
                      <button onClick={() => setAddingRole(null)} className="stui-related-header-btn"><X size={13} /></button>
                    </>
                  )}
                  {!showGlobalPerms && !addingRole && isEditing && databases.filter(d => !userDetail.databaseRoles?.some(dr => dr.databaseId === d.id)).length > 0 && (
                    <button onClick={() => setAddingRole({ UserId: selected.id, DatabaseId: 0, Role: 'User' })} title="Add role" className="stui-related-header-btn"><Plus size={14} /></button>
                  )}
                  {showGlobalPerms && addingGlobalPerm && (
                    <>
                      <button onClick={handleAddGlobalPerm} disabled={!newGlobalPermDefId} className="stui-related-header-btn"><Save size={13} /></button>
                      <button onClick={() => { setAddingGlobalPerm(false); setNewGlobalPermDefId(''); }} className="stui-related-header-btn"><X size={13} /></button>
                    </>
                  )}
                  {showGlobalPerms && !addingGlobalPerm && isEditing && (
                    <button onClick={() => { setAddingGlobalPerm(true); setNewGlobalPermDefId(''); }} title="Add global permission" className="stui-related-header-btn"><Plus size={14} /></button>
                  )}
                  {/* Globe/Database toggle — always far right */}
                  <span
                    onClick={() => { setShowGlobalPerms(!showGlobalPerms); setAddingGlobalPerm(false); setNewGlobalPermDefId(''); setAddingRole(null); }}
                    title={showGlobalPerms ? 'Show instance roles' : 'Show global permissions'}
                    style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer', flexShrink: 0 }}
                  >
                    {showGlobalPerms
                      ? <Database size={14} style={{ color: 'rgba(255,255,255,0.85)' }} />
                      : <Globe size={14} style={{ color: globalPerms.length > 0 ? 'var(--color-warning)' : 'rgba(255,255,255,0.6)' }} />
                    }
                    {!showGlobalPerms && globalPerms.length > 0 && (
                      <span style={{ position: 'absolute', bottom: '-4px', right: '-6px', background: 'var(--color-warning)', color: 'white', borderRadius: '50%', minWidth: '12px', height: '12px', fontSize: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 2px' }}>
                        {globalPerms.length}
                      </span>
                    )}
                  </span>
                </div>
                <div className="stui-related-content">
                  {showGlobalPerms ? (
                    <>
                      <div style={{ flex: 1 }}>
                        <div className="stui-data-grid-container">
                          <div className="stui-data-grid-scroll-area">
                            <table className="stui-data-grid">
                              <thead><tr><th>Permission</th><th style={{ width: '120px' }}>Category</th><th style={{ width: '80px' }}>Access</th>{isEditing && <th style={{ width: '48px' }}></th>}</tr></thead>
                              <tbody>
                                {globalPerms.length === 0 ? (
                                  <tr><td colSpan={isEditing ? 4 : 3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>No global permissions assigned</td></tr>
                                ) : globalPerms.map(gp => (
                                  <tr key={gp.id}>
                                    <td style={{ fontWeight: 500 }}>{gp.displayName}</td>
                                    <td>{gp.category}</td>
                                    <td><span style={{ fontWeight: 600, color: 'var(--color-success)' }}>{gp.accessType}</span></td>
                                    {isEditing && <td style={{ textAlign: 'center' }}><button onClick={() => handleDeleteGlobalPerm(gp)} className="stui-related-header-btn danger"><Trash2 size={13} /></button></td>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <div className="stui-data-grid-container">
                          <div className="stui-data-grid-scroll-area">
                            <table className="stui-data-grid">
                              <thead><tr><th style={{ width: '120px' }}>Instance</th><th>Description</th><th style={{ width: '120px' }}>Role</th><th style={{ width: '100px' }}>Permissions</th></tr></thead>
                              <tbody>
                                {userDetail.databaseRoles?.map(dr => {
                                  const displayRole = dirtyRoles[dr.id] || dr.role;
                                  const isRoleDirty = !!dirtyRoles[dr.id];
                                  return (
                                    <tr key={dr.id} className="stui-clickable" onClick={() => handleSelectDbRole(dr)} onDoubleClick={() => { if (isEditing) setEditingRoleId(dr.id); }} style={selectedDbRole?.id === dr.id ? { backgroundColor: 'var(--color-primary-light)' } : undefined}>
                                      <td>{dr.databaseName}</td>
                                      <td>{databases.find(d => d.id === dr.databaseId)?.description || ''}</td>
                                      <td><span style={{ fontWeight: 600, backgroundColor: isRoleDirty ? '#fffde7' : undefined, borderBottom: isRoleDirty ? '2px solid #f9a825' : undefined, padding: isRoleDirty ? '1px 4px' : undefined }}>{displayRole}</span></td>
                                      <td>{dr.overrides?.length || 0}</td>
                                    </tr>
                                  );
                                })}
                                {(!userDetail.databaseRoles || userDetail.databaseRoles.length === 0) && (
                                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>No roles assigned</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* INSTANCE PERMISSIONS */}
              {!showGlobalPerms && <div className="stui-related-section" style={{ flex: 1, minHeight: '150px', marginTop: 'var(--spacing-md)', display: 'flex', flexDirection: 'column' }}>
                <div className="stui-detail-section-header stui-related-header" style={{ opacity: selectedDbRole ? 1 : 0.4, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Left: title or permission dropdown */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {!addingPerm && (
                      <span>Instance Permissions{selectedDbRole ? ` — ${selectedDbRole.databaseName} (${selectedDbRole.role})` : ' (select instance role)'}</span>
                    )}
                    {addingPerm && (
                      <select className="stui-field-input" value={newPermDefId} onChange={e => setNewPermDefId(e.target.value)} style={{ width: '100%', height: '24px', padding: '0 4px', fontSize: 'var(--text-xs)' }}>
                        <option value="">Select permission...</option>
                        {allPermissions.filter(p => !selectedDbRole?.overrides?.some(o => o.permissionDefId === p.id)).map(p => <option key={p.id} value={p.id}>{p.displayName} ({p.category})</option>)}
                      </select>
                    )}
                  </div>
                  {/* Right cluster: Trash | Save+Cancel or Plus — immediately left of ScanEye */}
                  {!addingPerm && isEditing && selectedOverride && (
                    <button onClick={handleDeleteOverride} title="Remove selected override" className="stui-related-header-btn danger"><Trash2 size={14} /></button>
                  )}
                  {addingPerm && (
                    <button onClick={handleAddInstancePerm} disabled={!newPermDefId} className="stui-related-header-btn"><Save size={13} /></button>
                  )}
                  {addingPerm ? (
                    <button onClick={() => { setAddingPerm(false); setNewPermDefId(''); }} className="stui-related-header-btn"><X size={13} /></button>
                  ) : (
                    isEditing && selectedDbRole && (
                      <button onClick={() => { setAddingPerm(true); setNewPermDefId(''); }} title="Add permission override" className="stui-related-header-btn"><Plus size={14} /></button>
                    )
                  )}
                  {/* ScanEye — always far right when a role is selected */}
                  {selectedDbRole && (
                    <button onClick={() => setShowEffective(true)} title="View effective permissions" className="stui-related-header-btn"><ScanEye size={16} /></button>
                  )}
                </div>
                <div className="stui-related-content">
                  {selectedDbRole ? (
                    <div style={{ flex: 1 }}>
                      <div className="stui-data-grid-container">
                        <div className="stui-data-grid-scroll-area">
                          <table className="stui-data-grid">
                            <thead><tr><th>Permission</th><th>Category</th><th>Access</th></tr></thead>
                            <tbody>
                              {resolvedPerms ? (() => {
                                const overrides = resolvedPerms.permissions?.filter(p => p.source === 'UserOverride') || [];
                                if (overrides.length === 0) return <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>No overrides — using {selectedDbRole.role} role defaults</td></tr>;
                                return overrides.map((p, idx) => (
                                  <tr key={idx} className="stui-clickable" onClick={() => setSelectedOverride(selectedOverride?.permissionKey === p.permissionKey ? null : p)} style={selectedOverride?.permissionKey === p.permissionKey ? { backgroundColor: 'var(--color-primary-light)' } : undefined}>
                                    <td style={{ fontWeight: 500 }}>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.displayName || p.permissionKey}</td>
                                    <td>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.category || ''}</td>
                                    <td><span style={{ fontWeight: 600, color: p.access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)' }}>{p.access}</span></td>
                                  </tr>
                                ));
                              })() : <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>Loading...</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-gray-300)', fontSize: 'var(--text-sm)' }} />
                  )}
                </div>
              </div>}
            </div>
          ) : (
            <div className="stui-detail-empty">
              <Eye size={48} />
              <p>Select a user to view details</p>
              <p className="stui-hint">Manage roles, permissions, and organization assignment</p>
            </div>
          )}
        </div>
      </div>

      {/* NEW USER MODAL */}
      {showNewUser && (
        <div className="stui-fk-select-overlay">
          <div className="stui-fk-select" style={{ maxWidth: '520px' }}>
            <div className="stui-modal-header">
              <h3>New User</h3>
              <button className="stui-modal-close" onClick={() => setShowNewUser(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid" style={{ gap: 'var(--spacing-md)' }}>
                <div className="stui-field-group stui-col-12">
                  <label>First Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                  <input className="stui-field-input" value={newUser.firstName} onChange={e => setNewUser(p => ({ ...p, firstName: e.target.value }))} placeholder="First" autoFocus />
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Last Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                  <input className="stui-field-input" value={newUser.lastName} onChange={e => setNewUser(p => ({ ...p, lastName: e.target.value }))} placeholder="Last" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Display Name (auto)</label>
                  <input className="stui-field-input" value={newUser.lastName && newUser.firstName ? `${newUser.lastName.trim()}, ${newUser.firstName.trim()}` : ''} readOnly style={{ color: 'var(--color-gray-500)', fontStyle: newUser.lastName || newUser.firstName ? 'normal' : 'italic' }} placeholder="Last, First (generated)" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Email Address <span style={{ color: 'var(--color-error)' }}>*</span></label>
                  <input className="stui-field-input" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com" type="email" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Organization</label>
                  <select className="stui-field-input" value={newUser.organizationId} onChange={e => setNewUser(p => ({ ...p, organizationId: e.target.value }))}>
                    <option value="">-- None --</option>
                    {organizations.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
                  </select>
                </div>
                <div className="stui-field-group stui-col-16">
                  <label>Default Database</label>
                  <select className="stui-field-input" value={newUser.defaultDatabaseId} onChange={e => setNewUser(p => ({ ...p, defaultDatabaseId: e.target.value }))}>
                    <option value="">-- None --</option>
                    {databases.map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
                  </select>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Default Role</label>
                  <select className="stui-field-input" value={newUser.defaultRole} onChange={e => setNewUser(p => ({ ...p, defaultRole: e.target.value }))} disabled={!newUser.defaultDatabaseId}>
                    <option value="Guest">Guest</option>
                    <option value="User">User</option>
                    <option value="PowerUser">PowerUser</option>
                    <option value="AppAdmin">AppAdmin</option>
                  </select>
                </div>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--color-gray-400)', marginTop: 'var(--spacing-md)', marginBottom: 0 }}>
                After creating the user, select them and use <strong>Send Invite</strong> to generate an invitation link.
              </p>
            </div>
            <div className="stui-modal-footer">
              <button className="btn stui-btn-secondary" onClick={() => setShowNewUser(false)}>Cancel</button>
              <button className="btn stui-btn-primary" onClick={handleCreateUser} disabled={newUserSaving || !newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim()}>
                {newUserSaving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COPY FROM USER MODAL */}
      {showCopyModal && (
        <div className="stui-fk-select-overlay">
          <div className="stui-fk-select" style={{ maxWidth: '500px' }}>
            <div className="stui-modal-header">
              <h3>Copy Roles & Permissions</h3>
              <button className="stui-modal-close" onClick={() => { setShowCopyModal(false); setCopySource(null); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-lg)' }}>Copy roles and permission overrides from another user to <strong>{userDetail?.displayName}</strong>.</p>
              <div className="stui-field-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Copy from</label>
                <select className="stui-field-input" value={copySource?.id || ''} onChange={e => { const u = users.find(u => u.id === parseInt(e.target.value)); setCopySource(u || null); }}>
                  <option value="">Select a user...</option>
                  {users.filter(u => u.id !== selected?.id).map(u => <option key={u.id} value={u.id}>{u.displayName}{u.email ? ` (${u.email})` : ''}</option>)}
                </select>
              </div>
              <div className="stui-field-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Scope</label>
                <select className="stui-field-input" value={copyDbScope} onChange={e => setCopyDbScope(e.target.value)}>
                  <option value="all">All instances (full mirror)</option>
                  {databases.map(d => <option key={d.id} value={d.id}>{d.displayName} only</option>)}
                </select>
              </div>
              {copySource && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', padding: 'var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)' }}>
                  {copyDbScope === 'all'
                    ? <span>This will replace ALL roles and permission overrides on <strong>{userDetail?.displayName}</strong> with those from <strong>{copySource.displayName}</strong>.</span>
                    : <span>This will copy the role and permissions for <strong>{databases.find(d => d.id === parseInt(copyDbScope))?.displayName}</strong> from <strong>{copySource.displayName}</strong>.</span>
                  }
                </div>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="btn stui-btn-secondary" onClick={() => { setShowCopyModal(false); setCopySource(null); }}>Cancel</button>
              <button className="btn stui-btn-primary" onClick={handleCopyFrom} disabled={!copySource || copySaving}>{copySaving ? 'Copying...' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT INSTANCE ROLE MODAL */}
      {editingRoleId && (() => {
        const dr = userDetail?.databaseRoles?.find(r => r.id === editingRoleId);
        if (!dr) return null;
        const currentValue = dirtyRoles[dr.id] || dr.role;
        return (
          <div className="stui-fk-select-overlay">
            <div className="stui-fk-select" style={{ maxWidth: '340px' }}>
              <div className="stui-modal-header">
                <h3>Edit Role — {dr.databaseName}</h3>
                <button className="stui-modal-close" onClick={() => setEditingRoleId(null)}><X size={20} /></button>
              </div>
              <div className="stui-modal-body">
                <div className="stui-field-group">
                  <label>Role</label>
                  <select className="stui-field-input" value={currentValue} onChange={e => { const newRole = e.target.value; if (newRole === dr.role) { const next = { ...dirtyRoles }; delete next[dr.id]; setDirtyRoles(next); } else { setDirtyRoles({ ...dirtyRoles, [dr.id]: newRole }); } setEditingRoleId(null); }} autoFocus>
                    <option value="Guest">Guest</option><option value="User">User</option><option value="PowerUser">PowerUser</option><option value="AppAdmin">AppAdmin</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* EFFECTIVE PERMISSIONS MODAL */}
      {showEffective && selectedDbRole && resolvedPerms && (
        <div className="stui-fk-select-overlay">
          <div className="stui-fk-select" style={{ maxWidth: '650px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="stui-modal-header">
              <h3>Effective Permissions — {userDetail?.displayName} / {selectedDbRole.databaseName}</h3>
              <button className="stui-modal-close" onClick={() => setShowEffective(false)}><X size={20} /></button>
            </div>
            {(() => {
              const globalRows = (resolvedPerms.globalPermissions || []).map(key => ({
                permissionKey: key, access: 'Grant', source: 'Global'
              }));
              const dbRows = (resolvedPerms.permissions || []);
              const allRows = [...dbRows, ...globalRows];
              const totalCount = allRows.length;
              const filtered = allRows
                .filter(p => !hideEffectiveDenied || p.access !== 'Deny')
                .slice()
                .sort((a, b) => {
                  const da = allPermissions.find(ap => ap.permissionKey === a.permissionKey);
                  const db2 = allPermissions.find(ap => ap.permissionKey === b.permissionKey);
                  const cat = (da?.category || '').localeCompare(db2?.category || '');
                  if (cat !== 0) return cat;
                  return (da?.displayName || a.permissionKey).localeCompare(db2?.displayName || b.permissionKey);
                });
              return (
                <>
                  <div className="stui-modal-body" style={{ flex: 1, overflow: 'auto', padding: 0, paddingLeft: 'var(--spacing-lg)' }}>
                    <table className="stui-data-grid" style={{ fontSize: 'var(--text-sm)' }}>
                      <thead><tr><th style={{ paddingLeft: 'var(--spacing-xl)' }}>Category</th><th>Permission</th><th style={{ width: '80px' }}>Access</th><th style={{ width: '110px' }}>Source</th></tr></thead>
                      <tbody>
                        {filtered.map((p, idx) => {
                          const def = allPermissions.find(ap => ap.permissionKey === p.permissionKey);
                          const sourceLabel = p.source === 'UserOverride' ? 'Override'
                            : p.source === 'RoleDefault' ? 'Role Default'
                            : p.source === 'SysAdmin' ? 'SysAdmin'
                            : p.source === 'Global' ? 'Global'
                            : 'No Entry';
                          const sourceColor = p.source === 'UserOverride' ? 'var(--color-warning)'
                            : p.source === 'Global' ? 'var(--color-primary)'
                            : p.source === 'NoEntry' ? 'var(--color-gray-300)'
                            : 'var(--color-gray-400)';
                          return (
                            <tr key={idx}>
                              <td style={{ paddingLeft: 'var(--spacing-xl)' }}>{def?.category || ''}</td>
                              <td style={{ fontWeight: 500 }}>{def?.displayName || p.permissionKey}</td>
                              <td><span style={{ fontWeight: 600, color: p.access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)' }}>{p.access}</span></td>
                              <td style={{ color: sourceColor }}>{sourceLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="stui-modal-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                      Role: <strong>{resolvedPerms.resolvedRole}</strong>{globalRows.length > 0 && <span> + <strong>{globalRows.length}</strong> global</span>} — showing {filtered.length} of {totalCount}
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', marginLeft: 'var(--spacing-lg)' }}>
                        <input type="checkbox" checked={hideEffectiveDenied} onChange={e => setHideEffectiveDenied(e.target.checked)} style={{ width: '13px', height: '13px' }} />
                        Hide denied
                      </label>
                    </span>
                    <button className="btn stui-btn-primary" onClick={() => setShowEffective(false)}>Close</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
